import React, { useState, useEffect, useCallback } from 'react';
import { useDisclosure, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Tooltip, Spinner } from '@nextui-org/react';
import AnimatedHeartIcon from './AnimatedHeartIcon';
import ReactPlayer from 'react-player';
import { Button } from '@nextui-org/button';
import { useAuth } from '../hooks/useAuth';
// REMOVED: import { useDocumentProgress } from '../hooks/useDocumentProgress';
import progressService from '../services/progressService';
import { useQueryClient } from '@tanstack/react-query';
import { syncCachesAfterSave } from '../hooks/progressCache';
import { trackEvent, trackProgressSave } from '../services/analytics';
import sessionStorageService, { ModalSessionData } from '../services/sessionStorageService';
import batchSaveService from '../services/batchSaveService';
import { TipTapEditor } from '../components/NotesEditor';
import SummaryDialog from './SummaryDialog';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheck } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { VIDEO_MODAL_CONFIG, CONFIRMATION_MODAL_CONFIG } from '../constants/modalConfig';

interface WatchVideoButtonProps {
    doc: any;
    collectionPath?: string;
    onProgressUpdate?: () => void;
    initialProgress?: {
        completionStatus: boolean;
        isFavorited: boolean;
        notes: any;
    };
}

const WatchVideoButton: React.FC<WatchVideoButtonProps> = ({ doc, collectionPath, onProgressUpdate, initialProgress }) => {
    const { isOpen, onOpen, onOpenChange } = useDisclosure();

    // Analytics: one event each time the lecture video modal opens
    useEffect(() => {
        if (isOpen) trackEvent('video_open', { lecture_name: doc.name, course_path: collectionPath?.replace(/^\/+/, '') });
    }, [isOpen]);
    const {
        isOpen: isConfirmOpen,
        onOpen: onConfirmOpen,
        onOpenChange: onConfirmOpenChange
    } = useDisclosure();
    const {
        isOpen: isFavoriteConfirmOpen,
        onOpen: onFavoriteConfirmOpen,
        onOpenChange: onFavoriteConfirmOpenChange
    } = useDisclosure();
    const { user, setShowAuthModal } = useAuth();
    const queryClient = useQueryClient();
    // REMOVED: const { notes, saveNote, completionStatus, toggleCompletion, isFavorited, toggleFavorite } = useDocumentProgress(doc.id, collectionPath);

    // Individual state management (no context)
    const [progress, setProgress] = useState<any>(initialProgress || {
        completionStatus: false,
        isFavorited: false,
        notes: null
    });

    // Update progress state when initialProgress prop changes
    useEffect(() => {
        if (initialProgress) {
            setProgress(initialProgress);
        }
    }, [initialProgress]);

    // Initialize session when modal opens
    useEffect(() => {
        if (isOpen && user && doc.id) {
            initializeSession();
        }
    }, [isOpen, user, doc.id]);

    // Initialize session data
    const initializeSession = useCallback(() => {
        if (!user || !doc.id) return;

        // Use initialProgress if available, otherwise use current progress state
        const progressData = initialProgress || progress;

        // Store original state from Firebase
        const original = {
            completionStatus: progressData?.completionStatus || false,
            isFavorited: progressData?.isFavorited || false,
            notes: progressData?.notes?.content || ""
        };
        setOriginalState(original);

        // Check for existing session
        const existingSession = sessionStorageService.getSession(user.uid, doc.id);

        if (existingSession) {
            // Load existing session
            setSessionData(existingSession);
            // Update UI to reflect session state without triggering useEffect
            setProgress((prev: any) => {
                // Only update if the values are actually different
                if (prev.completionStatus !== existingSession.currentState.completionStatus ||
                    prev.isFavorited !== existingSession.currentState.isFavorited ||
                    prev.notes?.content !== existingSession.currentState.notes) {
                    return {
                        ...prev,
                        completionStatus: existingSession.currentState.completionStatus,
                        isFavorited: existingSession.currentState.isFavorited,
                        notes: existingSession.currentState.notes ? { content: existingSession.currentState.notes } : null
                    };
                }
                return prev;
            });
            setNotesContent(existingSession.currentState.notes || "");
        } else {
            // Create new session
            const newSession: ModalSessionData = {
                userId: user.uid,
                docId: doc.id,
                collectionPath,
                originalState: original,
                currentState: original,
                timestamp: Date.now()
            };
            setSessionData(newSession);
            sessionStorageService.saveSession(user.uid, doc.id, collectionPath, original, original);
        }
    }, [user, doc.id, collectionPath, initialProgress]);

    // Extract values from progress state
    const notes = progress?.notes;
    const completionStatus = progress?.completionStatus || false;
    const isFavorited = progress?.isFavorited || false;

    const [showNotesEditor, setShowNotesEditor] = useState(false);
    const [notesContent, setNotesContent] = useState('');

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isToggling, setIsToggling] = useState(false);
    const [isFavoriteToggling, setIsFavoriteToggling] = useState(false);

    // Session management for deferred saves
    const [sessionData, setSessionData] = useState<ModalSessionData | null>(null);
    const [showSummaryDialog, setShowSummaryDialog] = useState(false);
    const [originalState, setOriginalState] = useState<any>(null);
    const [isBatchSaving, setIsBatchSaving] = useState(false);

    // Update notesContent when progress data changes
    useEffect(() => {
        if (progress?.notes?.content !== undefined) {
            setNotesContent(progress.notes.content);
        }
    }, [progress?.notes?.content]);

    // Prevent body scroll when modal is open
    React.useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        };
    }, [isOpen]);

    // Track content changes
    React.useEffect(() => {
        const originalContent = notes?.content || '';
        setHasUnsavedChanges(notesContent !== originalContent);
    }, [notesContent, notes?.content]);

    const handleNotesClick = () => {
        if (!user) {
            setShowAuthModal(true);
            return;
        }

        // Set initial content when opening editor
        setNotesContent(notes?.content || '');
        setShowNotesEditor(!showNotesEditor);
    };

    // Notes are now saved to session storage automatically
    const handleNotesChange = (content: string) => {
        setNotesContent(content);
        setHasUnsavedChanges(content !== (notes?.content || ''));

        // Update session storage
        if (sessionData) {
            const updatedSession = {
                ...sessionData,
                currentState: {
                    ...sessionData.currentState,
                    notes: content
                },
                timestamp: Date.now()
            };
            setSessionData(updatedSession);
            sessionStorageService.updateSessionState(user!.uid, doc.id, { notes: content });
        }
    };

    // Individual toggle functions for this component only - now using session storage
    const toggleCompletion = useCallback(async () => {
        if (!user || !sessionData) return;

        const newStatus = !completionStatus;

        // Optimistic update
        setProgress((prev: any) => ({
            ...prev,
            completionStatus: newStatus,
            completedAt: newStatus ? new Date() : null
        }));

        // Update session storage
        const updatedSession = {
            ...sessionData,
            currentState: {
                ...sessionData.currentState,
                completionStatus: newStatus
            },
            timestamp: Date.now()
        };
        setSessionData(updatedSession);
        sessionStorageService.updateSessionState(user.uid, doc.id, { completionStatus: newStatus });
    }, [user, doc.id, completionStatus, sessionData]);

    const toggleFavorite = useCallback(async () => {
        if (!user || !sessionData) return;

        const newStatus = !isFavorited;

        // Optimistic update
        setProgress((prev: any) => ({
            ...prev,
            isFavorited: newStatus
        }));

        // Update session storage
        const updatedSession = {
            ...sessionData,
            currentState: {
                ...sessionData.currentState,
                isFavorited: newStatus
            },
            timestamp: Date.now()
        };
        setSessionData(updatedSession);
        sessionStorageService.updateSessionState(user.uid, doc.id, { isFavorited: newStatus });
    }, [user, doc.id, isFavorited, sessionData]);

    const handleCancelNotes = () => {
        if (hasUnsavedChanges) {
            if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
                setShowNotesEditor(false);
                setNotesContent(notes?.content || '');
                setHasUnsavedChanges(false);
            }
        } else {
            setShowNotesEditor(false);
            setNotesContent(notes?.content || '');
        }
    };

    // Batch save functions
    const handleSaveChanges = async () => {
        if (!user || !sessionData) return;

        setIsBatchSaving(true);
        try {
            const changes = sessionStorageService.getChangeSummary(sessionData);
            const result = await batchSaveService.executeBatchSave(
                user.uid,
                doc.id,
                collectionPath,
                changes
            );

            if (result.success) {
                // Clear session storage
                sessionStorageService.clearSession(user.uid, doc.id);

                // Refresh progress data
                const updatedProgress = await progressService.fetchUserProgress(user.uid, doc.id, collectionPath);
                setProgress(updatedProgress);
                syncCachesAfterSave(queryClient, user.uid, doc.id, collectionPath, updatedProgress);
                trackProgressSave(changes, doc.name, collectionPath);

                // Show success toast
                toast.success('All changes saved successfully!');

                // Close modal
                onOpenChange();
                setShowSummaryDialog(false);
                setShowNotesEditor(false);
                setNotesContent(updatedProgress?.notes?.content || '');
                setHasUnsavedChanges(false);

                // Refresh parent progress data
                onProgressUpdate?.();
            } else {
                throw new Error(result.error);
            }
        } catch (error: any) {
            console.error('Error saving changes:', error);
            toast.error(batchSaveService.getErrorMessage(error.message));
        } finally {
            setIsBatchSaving(false);
        }
    };

    const handleDiscardChanges = () => {
        if (!user) return;

        // Clear session storage
        sessionStorageService.clearSession(user.uid, doc.id);

        // Reset to original state
        if (originalState) {
            setProgress((prev: any) => ({
                ...prev,
                completionStatus: originalState.completionStatus,
                isFavorited: originalState.isFavorited,
                notes: originalState.notes ? { content: originalState.notes } : null
            }));
            setNotesContent(originalState.notes || "");
        }

        // Close modal
        onOpenChange();
        setShowSummaryDialog(false);
        setShowNotesEditor(false);
        setHasUnsavedChanges(false);
    };

    const handleModalClose = () => {
        if (!user) {
            onOpenChange();
            return;
        }

        // Check if there are changes in session
        if (sessionData && sessionStorageService.hasChanges(sessionData)) {
            setShowSummaryDialog(true);
        } else {
            // No changes, close immediately
            onOpenChange();
            setShowNotesEditor(false);
            setNotesContent(notes?.content || '');
            setHasUnsavedChanges(false);
        }
    };

    const handleCompletionToggle = async () => {
        setIsToggling(true);
        try {
            await toggleCompletion();
            // Show success toast
            toast.success(completionStatus ? 'Marked as incomplete!' : 'Marked as complete!');
        } catch (error) {
            console.error('Error toggling completion:', error);
            toast.error('Failed to update completion status');
        } finally {
            setIsToggling(false);
            onConfirmOpenChange(); // Close confirmation dialog
        }
    };

    const handleCompletionClick = () => {
        if (!user) {
            setShowAuthModal(true);
            return;
        }
        onConfirmOpen();
    };

    const handleFavoriteToggle = async () => {
        setIsFavoriteToggling(true);
        try {
            await toggleFavorite();
            // Toast message is handled by ProgressContext, no need for duplicate
        } catch (error) {
            console.error('Error toggling favorite:', error);
            toast.error('Failed to update favorite status');
        } finally {
            setIsFavoriteToggling(false);
            onFavoriteConfirmOpenChange(); // Close confirmation dialog
        }
    };

    const handleFavoriteClick = () => {
        if (!user) {
            setShowAuthModal(true);
            return;
        }
        onFavoriteConfirmOpen();
    };

    return (
        <>
            {/* Watch Video Button */}
            <Button
                color='primary'
                variant='flat'
                onPress={onOpen}
                startContent={
                    <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
                        <path d='M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' />
                    </svg>
                }
            >
                Watch Video
            </Button>

            {/* Full-screen Video Modal */}
            <Modal
                isOpen={isOpen}
                onOpenChange={handleModalClose}
                {...VIDEO_MODAL_CONFIG}
            >
                <ModalContent className='overflow-hidden flex flex-col h-full'>
                    {(onClose) => (
                        <>
                            <ModalHeader className='flex justify-between items-center p-6'>
                                <h2 className='text-2xl font-bold text-darkBlue'>
                                    {doc.description}
                                </h2>

                                <div className='flex items-center gap-3'>
                                    {/* Heart Icon - Only for authenticated users */}
                                    {user && (
                                        <Tooltip
                                            content={isFavorited ? "Remove from favorites" : "Add to favorites"}
                                            placement='bottom'
                                        >
                                            <button
                                                onClick={handleFavoriteClick}
                                                disabled={isFavoriteToggling}
                                                className='p-0 transition-all duration-300 focus:outline-none max-h-[50px] overflow-visible mb-2'
                                                aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
                                                style={{
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: 'transparent',
                                                    border: 'none',
                                                    outline: 'none'
                                                }}
                                            >
                                                <AnimatedHeartIcon
                                                    isFavorited={isFavorited}
                                                    showUnfavorited={true}
                                                    onClick={handleFavoriteClick}
                                                    disabled={isFavoriteToggling}
                                                    size={60}
                                                    speed={0.75}
                                                    scale={1.75}
                                                />
                                            </button>
                                        </Tooltip>
                                    )}

                                    {/* Completion Button - Only for authenticated users */}
                                    {user && (
                                        <Button
                                            color='primary'
                                            variant={completionStatus ? 'solid' : 'solid'}
                                            className={`${completionStatus
                                                ? 'bg-success hover:bg-success-600'
                                                : 'bg-primary hover:bg-primary-600'
                                                } transition-all duration-300 shadow-lg mr-6`}
                                            onClick={handleCompletionClick}
                                            isLoading={isToggling}
                                            startContent={completionStatus ? <FaCheck className='w-4 h-4' /> : null}
                                        >
                                            {completionStatus ? 'Completed' : 'Mark as Complete'}
                                        </Button>
                                    )}
                                </div>
                            </ModalHeader>
                            <ModalBody className='flex-1 min-h-0'>
                                <div className='relative h-full flex overflow-hidden'>
                                    {/* Video Section */}
                                    <motion.div
                                        className='flex items-center justify-center bg-black overflow-hidden'
                                        animate={{
                                            width: showNotesEditor ? '70%' : '100%',
                                            x: 0
                                        }}
                                        initial={false}
                                        transition={{
                                            duration: 0.5,
                                            ease: [0.4, 0, 0.2, 1]
                                        }}
                                    >
                                        <div className='relative w-full h-full flex items-center justify-center p-4'>
                                            <div className='relative w-full max-w-6xl' style={{ maxHeight: 'calc(100% - 2rem)' }}>
                                                <div className='relative w-full' style={{ paddingBottom: '56.25%' }}>
                                                    <ReactPlayer
                                                        url={doc.youtubeLink}
                                                        controls={true}
                                                        width='100%'
                                                        height='100%'
                                                        className='absolute top-0 left-0'
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* Notes Section */}
                                    <AnimatePresence>
                                        {showNotesEditor && (
                                            <motion.div
                                                className='bg-content1 border-l border-divider h-full'
                                                initial={{
                                                    width: 0,
                                                    opacity: 0
                                                }}
                                                animate={{
                                                    width: '30%',
                                                    opacity: 1
                                                }}
                                                exit={{
                                                    width: 0,
                                                    opacity: 0
                                                }}
                                                transition={{
                                                    duration: 0.5,
                                                    ease: [0.4, 0, 0.2, 1],
                                                    width: {
                                                        duration: 0.5
                                                    },
                                                    opacity: {
                                                        duration: 0.3,
                                                        delay: 0.1
                                                    }
                                                }}
                                            >
                                                <div className='p-6 h-full flex flex-col'>
                                                    <div className='flex items-center justify-between mb-4'>
                                                        <h3 className='text-xl font-semibold'>Your Notes</h3>
                                                        <motion.div
                                                            className='flex gap-2'
                                                            initial={{ opacity: 0 }}
                                                            animate={{
                                                                opacity: 1,
                                                                transition: {
                                                                    duration: 0.2,
                                                                    delay: 0.4,
                                                                    ease: [0.4, 0, 0.2, 1]
                                                                }
                                                            }}
                                                            exit={{
                                                                opacity: 0,
                                                                transition: {
                                                                    duration: 0.2,
                                                                    delay: 0,
                                                                    ease: [0.4, 0, 0.2, 1]
                                                                }
                                                            }}
                                                        >
                                                            <Button
                                                                color='danger'
                                                                onPress={handleCancelNotes}
                                                            >
                                                                Cancel
                                                            </Button>
                                                            <div className='flex items-center text-sm text-default-500'>
                                                                Changes saved to session
                                                            </div>
                                                        </motion.div>
                                                    </div>
                                                    <div className='flex-1 overflow-y-auto'>
                                                        <TipTapEditor
                                                            content={notesContent}
                                                            onChange={handleNotesChange}
                                                            placeholder='Write your notes about this video...'
                                                            className='shadow-sm min-h-full'
                                                        />
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </ModalBody>
                            <ModalFooter className='flex items-center justify-between'>
                                {showNotesEditor ? (
                                    <div className='w-px' />
                                ) : (
                                    <div className='w-px' />
                                )}

                                <div className='flex gap-2'>
                                    {user && (
                                        <Button
                                            color='primary'
                                            onClick={handleNotesClick}
                                            startContent={
                                                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' />
                                                </svg>
                                            }
                                        >
                                            {showNotesEditor ? 'Hide Notes' : (notes?.content ? 'Edit Notes' : 'Add Notes')}
                                        </Button>
                                    )}
                                    <Button
                                        color='danger'
                                        onPress={onClose}
                                        isDisabled={isBatchSaving}
                                        startContent={isBatchSaving ? <Spinner size="sm" /> : undefined}
                                    >
                                        {isBatchSaving ? 'Saving...' : 'Close'}
                                    </Button>
                                </div>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {/* Confirmation Dialog */}
            <Modal
                isOpen={isConfirmOpen}
                onOpenChange={onConfirmOpenChange}
                {...CONFIRMATION_MODAL_CONFIG}
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className='flex flex-col gap-1'>
                                <h3 className='text-lg font-semibold text-darkBlue'>
                                    {completionStatus ? 'Mark as Incomplete?' : 'Mark as Complete?'}
                                </h3>
                            </ModalHeader>
                            <ModalBody>
                                <p className='text-default-600'>
                                    {completionStatus
                                        ? 'Are you sure you want to mark this video as incomplete?'
                                        : 'Are you sure you want to mark this video as complete?'
                                    }
                                </p>
                            </ModalBody>
                            <ModalFooter>
                                <Button
                                    color='danger'
                                    variant='light'
                                    onPress={onClose}
                                    isDisabled={isToggling}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    color='primary'
                                    onPress={handleCompletionToggle}
                                    isLoading={isToggling}
                                    className={completionStatus ? 'bg-warning' : 'bg-success'}
                                >
                                    {completionStatus ? 'Mark Incomplete' : 'Mark Complete'}
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {/* Favorite Confirmation Dialog */}
            <Modal
                isOpen={isFavoriteConfirmOpen}
                onOpenChange={onFavoriteConfirmOpenChange}
                {...CONFIRMATION_MODAL_CONFIG}
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className='flex flex-col gap-1'>
                                <h3 className='text-lg font-semibold text-darkBlue'>
                                    {isFavorited ? 'Remove from Favorites?' : 'Add to Favorites?'}
                                </h3>
                            </ModalHeader>
                            <ModalBody>
                                <p className='text-default-600'>
                                    {isFavorited
                                        ? 'Are you sure you want to remove this video from your favorites?'
                                        : 'Are you sure you want to add this video to your favorites?'
                                    }
                                </p>
                            </ModalBody>
                            <ModalFooter>
                                <Button
                                    color='danger'
                                    variant='light'
                                    onPress={onClose}
                                    isDisabled={isFavoriteToggling}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    color='primary'
                                    onPress={handleFavoriteToggle}
                                    isLoading={isFavoriteToggling}
                                    className={isFavorited ? 'bg-warning' : 'bg-danger'}
                                >
                                    {isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {/* Summary Dialog */}
            <SummaryDialog
                isOpen={showSummaryDialog}
                onClose={() => setShowSummaryDialog(false)}
                onSave={handleSaveChanges}
                onDiscard={handleDiscardChanges}
                changes={sessionData ? sessionStorageService.getChangeSummary(sessionData) : {}}
                isSaving={isBatchSaving}
            />
        </>
    );
};

export default WatchVideoButton; 